import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Phone, Globe, Instagram, MapPin, Star, Clock, Beer, Bone, MessageSquareText, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";
import VenueMap from "../components/VenueMap";
import CouponCard from "../components/CouponCard";
import ReviewDialog from "../components/ReviewDialog";
import ReviewCard from "../components/ReviewCard";
import ShareButton from "../components/ShareButton";

const PRICE_LABELS = { 1: "$", 2: "$$", 3: "$$$", 4: "$$$$" };
const DAYS = [
  ["mon", "lun", "LUN"], ["tue", "mar", "MAR"], ["wed", "mie", "MIÉ"],
  ["thu", "jue", "JUE"], ["fri", "vie", "VIE"], ["sat", "sab", "SÁB"], ["sun", "dom", "DOM"],
];

function buildMapsUrl(v) {
  if (v?.lat != null && v?.lng != null) {
    return `https://maps.google.com/maps?q=${v.lat},${v.lng}+(${encodeURIComponent(v.name || "")})`;
  }
  if (v?.address) {
    return `https://maps.google.com/maps?q=${encodeURIComponent(v.address + ", Santiago, Chile")}`;
  }
  return null;
}

function daysSince(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
}

function verifiedLabel(iso) {
  const n = daysSince(iso);
  if (n == null) return null;
  if (n === 0) return "Verificado hoy";
  if (n === 1) return "Verificado hace 1 día";
  return `Verificado hace ${n} días`;
}

export default function VenueDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [venue, setVenue] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [showReview, setShowReview] = useState(false);

  const loadAll = useCallback(() => {
    api.getVenue(id).then(setVenue).catch(() => {});
    api.listReviews(id).then(setReviews).catch(() => {});
    api.listCoupons({ venue_id: id }).then(setCoupons).catch(() => {});
  }, [id]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  if (!venue) {
    return (
      <div className="max-w-3xl mx-auto p-12">
        <div className="beer-pour" />
        <div className="mt-2 font-mono text-xs uppercase tracking-widest text-[#A3A3A3]">Cargando local…</div>
      </div>
    );
  }

  return (
    <div data-testid="venue-detail-page">
      {/* HERO */}
      <section className="relative h-[40vh] min-h-[300px] overflow-hidden border-b-2 border-[#1A1A1A]">
        {venue.cover_photo && (
          <img src={venue.cover_photo} alt={venue.name} className="w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-black/60 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 max-w-7xl mx-auto px-4 lg:px-8 pb-6">
          <div className="flex items-center gap-2">
            {venue.featured && <span className="bg-[#FEBA01] text-black font-display text-xs uppercase px-2 py-1">Top Kiltro</span>}
            <span className="tag-muted">{PRICE_LABELS[venue.price_level]}</span>
          </div>
          <h1 className="mt-3 font-display text-5xl sm:text-7xl uppercase tracking-tighter leading-[0.9] text-white">
            {venue.name}
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-xs uppercase tracking-widest text-[#A3A3A3]">
            <span className="flex items-center gap-1"><MapPin size={12} /> {venue.neighborhood}</span>
            <span className="flex items-center gap-1 text-[#FEBA01]">
              <Star size={12} className="fill-[#FEBA01]" /> {venue.rating_avg.toFixed(1)} ({venue.rating_count})
            </span>
            <span className="text-[10px] text-[#7C7C7C]">Fuente · {venue.rating_source || "Google"}</span>
            {verifiedLabel(venue.last_verified_at) && (
              <span
                className="inline-flex items-center gap-1 text-[#FEBA01] text-[10px] uppercase tracking-widest border border-[#FEBA01]/40 px-2 py-0.5"
                title={`Última verificación: ${venue.last_verified_at?.slice(0, 10)}${venue.verified_source ? ` · Fuente: ${venue.verified_source}` : ""}`}
                data-testid="venue-verified-badge"
              >
                <ShieldCheck size={11} /> {verifiedLabel(venue.last_verified_at)}
              </span>
            )}
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8 grid lg:grid-cols-[1fr_360px] gap-8">
        <main className="space-y-8">
          <p className="text-lg text-[#F5F5F5] leading-relaxed">{venue.description}</p>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setShowReview(true)}
              className="btn-brutal bg-[#FEBA01] text-black font-display uppercase text-lg px-6 py-3 shadow-brutal-white inline-flex items-center gap-2"
              data-testid="venue-review-btn"
            >
              <MessageSquareText size={18} /> Reseñar
            </button>
            <ShareButton
              kind="venue"
              id={venue.id}
              label="Compartir"
              className="text-lg px-6 py-3"
              testid="venue-share-btn"
            />
          </div>

          {/* Beer types */}
          {venue.beer_types?.length > 0 && (
            <section>
              <h2 className="font-display text-2xl uppercase tracking-tight mb-3">Tipos de cerveza</h2>
              <div className="flex flex-wrap gap-2">
                {venue.beer_types.map((b) => <span key={b} className="tag">{b}</span>)}
              </div>
            </section>
          )}

          {/* Menu removed — prices can change, avoiding misleading info */}

          {/* Huesos del local */}
          {coupons.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Bone size={20} strokeWidth={2.2} className="text-[#FEBA01]" />
                <h2 className="font-display text-2xl uppercase tracking-tight">{t("huesos.venueSection")}</h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {coupons.map((c) => <CouponCard key={c.id} coupon={c} />)}
              </div>
            </section>
          )}

          {/* Map */}
          <section>
            <h2 className="font-display text-2xl uppercase tracking-tight mb-3">Cómo llegar</h2>
            <VenueMap venues={[venue]} height={300} />
            <div className="mt-2 font-mono text-xs uppercase tracking-widest text-[#A3A3A3]">{venue.address}</div>
          </section>

          {/* Reviews */}
          <section>
            <h2 className="font-display text-2xl uppercase tracking-tight mb-3">Reseñas ({reviews.length})</h2>
            {reviews.length === 0 ? (
              <div className="border-2 border-dashed border-[#333] p-6 text-center text-[#A3A3A3] font-mono uppercase text-xs tracking-widest">
                Sin reseñas todavía. Sé el primer kiltro.
              </div>
            ) : (
              <div className="space-y-3">
                {[...reviews]
                  .sort((a, b) => (b.helpful_count || 0) - (a.helpful_count || 0))
                  .map((r) => (
                    <ReviewCard
                      key={r.id}
                      review={r}
                      onDeleted={(rid) => setReviews((prev) => prev.filter((x) => x.id !== rid))}
                    />
                  ))}
              </div>
            )}
          </section>
        </main>

        <aside className="space-y-4">
          <div className="bg-[#1A1A1A] border-2 border-[#333] p-5">
            <h3 className="font-display text-xl uppercase tracking-tight">Información</h3>
            <div className="mt-3 space-y-2 text-sm">
              <Row icon={MapPin} text={venue.address} href={buildMapsUrl(venue)} />
              {venue.phone && <Row icon={Phone} text={venue.phone} href={`tel:${venue.phone}`} />}
              {venue.website && <Row icon={Globe} text={venue.website.replace(/^https?:\/\//, "")} href={venue.website} />}
              {venue.instagram && <Row icon={Instagram} text={venue.instagram} href={`https://instagram.com/${venue.instagram.replace("@", "")}`} />}
            </div>
          </div>

          <div className="bg-[#1A1A1A] border-2 border-[#333] p-5">
            <div className="flex items-center gap-2 mb-3">
              <Clock size={16} className="text-[#FEBA01]" />
              <h3 className="font-display text-xl uppercase tracking-tight">Horarios</h3>
            </div>
            {venue.hours && Object.keys(venue.hours).length > 0 ? (
              <div className="grid grid-cols-[40px_1fr] gap-y-1 font-mono text-xs">
                {DAYS.map(([enKey, esKey, label]) => (
                  <React.Fragment key={enKey}>
                    <div className="text-[#FEBA01] uppercase">{label}</div>
                    <div className="text-[#A3A3A3] uppercase">{venue.hours[enKey] || venue.hours[esKey] || "—"}</div>
                  </React.Fragment>
                ))}
              </div>
            ) : (
              <div className="font-mono text-xs text-[#7C7C7C] italic">
                Horarios no disponibles por este local por el momento.
              </div>
            )}
          </div>

          {venue.ambient?.length > 0 && (
            <div className="bg-[#1A1A1A] border-2 border-[#333] p-5">
              <h3 className="font-display text-xl uppercase tracking-tight mb-3">Ambiente</h3>
              <div className="flex flex-wrap gap-2">
                {venue.ambient.map((a) => <span key={a} className="tag-muted">{a}</span>)}
              </div>
            </div>
          )}

          <Link
            to="/descubrir"
            className="block text-center font-mono text-xs uppercase tracking-widest text-[#A3A3A3] hover:text-[#FEBA01]"
          >
            ← Volver a descubrir
          </Link>
        </aside>
      </div>

      <ReviewDialog
        open={showReview}
        onClose={() => setShowReview(false)}
        venue={venue}
        onDone={loadAll}
      />
    </div>
  );
}

function Row({ icon: Icon, text, href }) {
  const inner = (
    <span className="flex items-center gap-2 text-[#F5F5F5]">
      <Icon size={14} className="text-[#FEBA01] shrink-0" />
      <span className="break-words">{text}</span>
    </span>
  );
  if (href) return <a href={href} target="_blank" rel="noreferrer" className="block hover:text-[#FEBA01]">{inner}</a>;
  return <div>{inner}</div>;
}
